import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * A dropdown menu (menu button pattern). `trigger` is a function receiving
 * the props to spread on the button: ({ ref, ...props }) => <button …/>.
 * items: [{ id, label, ariaLabel, icon, onSelect, to, state, danger, disabled, current, nested } | 'separator'] —
 * `state` goes with a link's navigation, `current` marks the page shown
 * (aria-current), `nested` indents an entry under the one before it.
 * Keyboard: Enter/Space/ArrowDown open on the first item, ArrowUp on the
 * last; arrows, Home, End move; Esc and Tab close; focus returns to the button.
 */
export function Menu({ trigger, items, align = 'start', placement = 'bottom', label, className }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();
  const real = items.filter((item) => item && item !== 'separator' && !item.disabled);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  const openAt = (index) => {
    setActive(index);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const node = itemRefs.current[active];
    if (node) node.focus();
    return undefined;
  }, [open, active]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (listRef.current && !listRef.current.contains(event.target)
          && buttonRef.current && !buttonRef.current.contains(event.target)) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close]);

  const onButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAt(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(Math.max(real.length - 1, 0));
    }
  };

  const onListKeyDown = (event) => {
    const count = real.length;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key === 'Tab') { close(false); return; }
    if (count === 0) return; // every item disabled: nothing to move to
    // Space activates a link item as it does a button.
    if (event.key === ' ' && event.target.tagName === 'A') { event.preventDefault(); event.target.click(); return; }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => (i + 1) % count); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => (i - 1 + count) % count); }
    else if (event.key === 'Home') { event.preventDefault(); setActive(0); }
    else if (event.key === 'End') { event.preventDefault(); setActive(count - 1); }
    else if (event.key.length === 1 && /\S/.test(event.key)) {
      const letter = event.key.toLowerCase();
      const from = (active + 1) % count;
      for (let step = 0; step < count; step += 1) {
        const index = (from + step) % count;
        if (String(real[index].label).toLowerCase().startsWith(letter)) { setActive(index); break; }
      }
    }
  };

  const select = (item) => {
    close(!item.to);
    if (item.onSelect) item.onSelect();
  };

  let realIndex = -1;
  itemRefs.current = [];
  return (
    <div className={cx('ui-menu', className)}>
      {trigger({
        ref: buttonRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: () => (open ? close(false) : openAt(0)),
        onKeyDown: onButtonKeyDown,
      })}
      {open && (
        <ul
          ref={listRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className={cx('ui-menu__list', `ui-menu__list--${align}`, `ui-menu__list--${placement}`)}
          onKeyDown={onListKeyDown}
        >
          {items.map((item, index) => {
            if (!item) return null;
            if (item === 'separator') return <li key={`sep-${index}`} role="separator" className="ui-menu__sep" />;
            const Icon = item.icon;
            const content = <>{Icon && <Icon size={18} />}<span>{item.label}</span></>;
            if (item.disabled) {
              return (
                <li key={item.id || index} role="none">
                  <span role="menuitem" aria-disabled="true" className="ui-menu__item is-disabled">{content}</span>
                </li>
              );
            }
            realIndex += 1;
            const myIndex = realIndex;
            const common = {
              ref: (node) => { itemRefs.current[myIndex] = node; },
              role: 'menuitem',
              tabIndex: myIndex === active ? 0 : -1,
              className: cx('ui-menu__item', item.danger && 'is-danger', item.nested && 'ui-menu__item--nested'),
              'aria-current': item.current ? 'page' : undefined,
              'aria-label': item.ariaLabel,
              onMouseEnter: () => setActive(myIndex),
            };
            return (
              <li key={item.id || index} role="none">
                {item.to ? (
                  <Link to={item.to} state={item.state} {...common} onClick={() => select(item)}>{content}</Link>
                ) : (
                  <button type="button" {...common} onClick={() => select(item)}>{content}</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Menu;
